import Cookies from 'js-cookie';
import { ctxGeo } from 'utils/brijContext';
import { CookieSettings } from 'types/ProductDetailsType';
import { CookiesConsentType } from 'types/CookiesConsentType';
import {
  ConsentModeObject,
  mapCookiesConsentToConsentMode,
} from 'utils/consentUtils';
import {
  ConsentRegion,
  RegionalConsentDefault,
  resolveConsentRegion,
} from 'utils/consentRegion';
import {
  PrivacyOptOut,
  getPrivacyOptOut,
  applyOptOutInvariant,
  isAdminPreviewOrEmbedded,
} from 'utils/privacyOptOut';

export const COOKIE_CONSENT_CAPTURED_AT_COOKIE = 'cookieConsentCapturedAt';

export type CookieConsentSnapshot = ConsentModeObject & {
  source: 'banner' | 'geo-exempt-default' | 'default';
  capturedAt: string;
  allConsented: boolean;
  // SIG-114 opt-outs (utils/privacyOptOut). gpc: true / false / null;
  // doNotSell: true / null, never false. Either one being true denies the ad
  // trio and clears allConsented.
  gpc: boolean | null;
  doNotSell: true | null;
};

// window.mode is set asynchronously via postMessage, so embedded contexts are
// detected with the synchronous iframe check instead of isPreviewMode(). A
// failing check means a frame.
const isEmbeddedContext = (): boolean => {
  try {
    return typeof window !== 'undefined' && window.self !== window.top;
  } catch (error) {
    return true;
  }
};

// Single shared geo computation for the CookieBanner AND the event snapshot,
// so the two can never disagree on whether a banner was required or on the
// default that applies until the visitor decides (utils/consentRegion).
// Phase 2 semantics: geo comes only from the Redis context (the legacy
// brij-*-code geo cookies are no longer set). FAIL CLOSED: if the banner is
// enabled for this brand and we cannot determine the visitor's location,
// require the banner rather than silently auto-granting consent
// (compliance-safe default).
// EXCEPTION - embedded contexts (brand-dashboard admin registration /
// preview iframes): those never have a redirect-owned Redis session, so
// unknown geo is EXPECTED there, and a fail-closed banner would block the
// admin's flow (and could write an implicit consent onto the customer's
// record). Inside an iframe, unknown geo keeps the pre-Phase-2 behavior;
// a geo MATCH still shows the banner as before.
// Never throws: an unreadable geo is treated as unknown.
export const getConsentRegion = (
  cookieSettings?: CookieSettings | null
): ConsentRegion => {
  let countryCode: string | undefined;
  let regionCode: string | undefined;
  try {
    countryCode = ctxGeo('country_code');
    regionCode = ctxGeo('region_code');
  } catch (error) {
    console.error('CONSENT REGION GEO READ FAILED: ', error);
    countryCode = undefined;
    regionCode = undefined;
  }
  return resolveConsentRegion({
    cookieSettings,
    countryCode,
    regionCode,
    isEmbedded: isEmbeddedContext(),
  });
};

export const isCookieBannerGeoRequired = (
  cookieSettings?: CookieSettings | null
): boolean => getConsentRegion(cookieSettings).bannerRequired;

// The banner categories a regional default stands for.
export const getRegionalDefaultCookiesConsent = (
  defaultConsent: RegionalConsentDefault
): CookiesConsentType => {
  const granted = defaultConsent === 'granted';
  return {
    essential: true,
    functional: granted,
    analytics: granted,
    marketing: granted,
    social: granted,
  };
};

// Essential only: what a profile write records today without a banner
// decision, and what the page applies under a present but invalid cookie.
const getEssentialOnlyCookiesConsent = (): CookiesConsentType => ({
  essential: true,
  functional: false,
  analytics: false,
  marketing: false,
  social: false,
});

const isCookiesConsentDecision = (
  value: unknown
): value is CookiesConsentType =>
  !!value && typeof value === 'object' && !Array.isArray(value);

type StoredCookieConsent = {
  // A 'cookieConsent' value is present (or the jar could not be read).
  present: boolean;
  // The visitor's banner decision, when that value is a decision object.
  decision: CookiesConsentType | null;
};

// The 'cookieConsent' cookie as the snapshot tiers see it: absent (no value
// or an empty one), a banner decision, or present but not a decision (the
// page then stays all denied). Never throws: an unreadable jar counts as
// present but not a decision, so callers keep essential only rather than a
// regional default.
const readStoredCookieConsent = (): StoredCookieConsent => {
  let storedConsent: string | undefined;
  try {
    storedConsent = Cookies.get('cookieConsent');
  } catch (error) {
    return { present: true, decision: null };
  }
  if (!storedConsent) return { present: false, decision: null };
  try {
    const parsed = JSON.parse(storedConsent);
    return {
      present: true,
      decision: isCookiesConsentDecision(parsed) ? parsed : null,
    };
  } catch (error) {
    return { present: true, decision: null };
  }
};

// What a profile write records for a visitor without a consent cookie: the
// regional default on a brand whose banner is on; essential only, as today,
// on a brand whose banner is off (Privacy off).
const getUndecidedCookiesConsent = (
  cookieSettings?: CookieSettings | null
): CookiesConsentType =>
  cookieSettings?.enabled
    ? getRegionalDefaultCookiesConsent(
        getConsentRegion(cookieSettings).defaultConsent
      )
    : getEssentialOnlyCookiesConsent();

export type EffectiveConsent = {
  functional: boolean;
  analytics: boolean;
  social: boolean;
};

// The visitor's current choices for the profile opt-out write: the stored
// banner decision; all off under a present but invalid cookie; without a
// cookie, what a registration would record (getUndecidedCookiesConsent).
// Undefined when there is no cookie and the brand settings are not loaded;
// the server then records those categories as not consented. Never throws.
export const getEffectiveConsent = (
  cookieSettings: CookieSettings | null | undefined,
  settingsKnown: boolean
): EffectiveConsent | undefined => {
  try {
    const storedConsent = readStoredCookieConsent();
    let consent: CookiesConsentType | null = storedConsent.decision;
    if (!consent && storedConsent.present) {
      consent = getEssentialOnlyCookiesConsent();
    } else if (!consent && settingsKnown) {
      consent = getUndecidedCookiesConsent(cookieSettings);
    }
    if (!consent) return undefined;
    return {
      functional: consent.functional === true,
      analytics: consent.analytics === true,
      social: consent.social === true,
    };
  } catch (error) {
    console.error('EFFECTIVE CONSENT RESOLUTION FAILED: ', error);
    return undefined;
  }
};

// The cookies consent meta for start_registration: the stored decision as
// today. Without a consent cookie, the regional default (instead of all
// denied) on a brand whose banner is on, and today's essential only on a
// brand whose banner is off. A present but invalid cookie keeps essential
// only, as the snapshot does. Marketing is forced off while an opt-out
// applies. Null in admin registration, preview and embedded contexts, where
// the caller keeps today's value.
export const getRegistrationCookiesConsentMeta = (
  storedConsent: unknown,
  cookieSettings?: CookieSettings | null
): CookiesConsentType | null => {
  if (isAdminPreviewOrEmbedded()) return null;
  let meta: CookiesConsentType;
  if (isCookiesConsentDecision(storedConsent)) {
    meta = storedConsent;
  } else if (readStoredCookieConsent().present) {
    meta = getEssentialOnlyCookiesConsent();
  } else {
    meta = getUndecidedCookiesConsent(cookieSettings);
  }
  return getPrivacyOptOut().active ? { ...meta, marketing: false } : meta;
};

const computeAllConsented = (consentMode: ConsentModeObject): boolean =>
  consentMode.ad_storage === 'granted' &&
  consentMode.ad_user_data === 'granted' &&
  consentMode.ad_personalization === 'granted' &&
  consentMode.analytics_storage === 'granted' &&
  consentMode.functionality_storage === 'granted' &&
  consentMode.personalization_storage === 'granted';

const buildSnapshot = (
  consentMode: ConsentModeObject,
  source: CookieConsentSnapshot['source'],
  optOut: PrivacyOptOut,
  capturedAt?: string
): CookieConsentSnapshot =>
  applyOptOutInvariant({
    ...consentMode,
    source,
    capturedAt: capturedAt || new Date().toISOString(),
    allConsented: computeAllConsented(consentMode),
    gpc: optOut.gpc,
    doNotSell: optOut.doNotSell,
  });

const isAdminOrPreviewMode = (): boolean => {
  try {
    const mode = (window as any).mode;
    return mode === 'adminRegistration' || mode === 'preview';
  } catch (error) {
    return true;
  }
};

// Resolution mirrors what the page's gtag consent state actually is:
// 1. banner decision persisted in the 'cookieConsent' cookie -> map it;
// 2. no decision + banner not required -> all granted (matches the
//    CookieBanner all-grant effect; under Phase 2 fail-closed semantics this
//    means geo is KNOWN and not covered, or an embedded iframe context);
// 3. no decision + banner required (including unknown geo outside iframes,
//    per fail-closed) -> the regional default: Consent Mode defaults (all
//    denied, security granted - matches the gtag('consent','default') in
//    index.html) for a denied region, all granted for a granted region such
//    as California (matches the all-grant effect there).
// Then the GPC / Do Not Sell opt-out is stamped and denies the ad trio.
// Returns undefined in admin-registration/preview modes (the admin's own
// browser consent must never be stamped on a customer's events - the server
// fallback chain resolves the customer's stored consent instead) and on any
// unexpected error (never block event logging) - unless an opt-out is known,
// which is then stamped on an all-denied default instead of being dropped.
const getCookieConsent = (
  cookieSettings?: CookieSettings | null
): CookieConsentSnapshot | undefined => {
  // Read on its own first, so a failure below can never drop a known opt-out.
  let optOut: PrivacyOptOut = { gpc: null, doNotSell: null, active: false };
  try {
    optOut = getPrivacyOptOut();
  } catch (optOutError) {
    console.error('COOKIE CONSENT OPT-OUT READ FAILED: ', optOutError);
  }

  try {
    const mode = (window as any).mode;
    if (mode === 'adminRegistration' || mode === 'preview') return undefined;

    const storedConsent = Cookies.get('cookieConsent');
    if (storedConsent) {
      // Shape-guard the parse: 'null'/primitives/arrays are not a banner
      // decision - fall through to the geo tiers instead of fabricating one.
      let parsedConsent: CookiesConsentType | null = null;
      try {
        const parsed = JSON.parse(storedConsent);
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
          parsedConsent = parsed as CookiesConsentType;
        }
      } catch (parseError) {
        parsedConsent = null;
      }
      if (parsedConsent) {
        return buildSnapshot(
          mapCookiesConsentToConsentMode(parsedConsent),
          'banner',
          optOut,
          Cookies.get(COOKIE_CONSENT_CAPTURED_AT_COOKIE)
        );
      }
      // Present-but-invalid cookie: the CookieBanner all-grant effect is
      // suppressed by the cookie's mere presence, so gtag stays all-denied -
      // matching that (never overclaiming) means stamping 'default' here
      // rather than falling through to the geo-exempt all-grant tier.
      return buildSnapshot(
        mapCookiesConsentToConsentMode({
          essential: true,
          functional: false,
          analytics: false,
          marketing: false,
          social: false,
        }),
        'default',
        optOut
      );
    }

    const consentRegion = getConsentRegion(cookieSettings);

    if (!consentRegion.bannerRequired) {
      return buildSnapshot(
        mapCookiesConsentToConsentMode({
          essential: true,
          functional: true,
          analytics: true,
          marketing: true,
          social: true,
        }),
        'geo-exempt-default',
        optOut
      );
    }

    if (consentRegion.defaultConsent === 'granted') {
      return buildSnapshot(
        mapCookiesConsentToConsentMode({
          essential: true,
          functional: true,
          analytics: true,
          marketing: true,
          social: true,
        }),
        'default',
        optOut
      );
    }

    return buildSnapshot(
      mapCookiesConsentToConsentMode({
        essential: true,
        functional: false,
        analytics: false,
        marketing: false,
        social: false,
      }),
      'default',
      optOut
    );
  } catch (error) {
    console.error('Error resolving cookie consent snapshot:', error);
    if (optOut.active && !isAdminOrPreviewMode()) {
      try {
        return buildSnapshot(
          mapCookiesConsentToConsentMode({
            essential: true,
            functional: false,
            analytics: false,
            marketing: false,
            social: false,
          }),
          'default',
          optOut
        );
      } catch (fallbackError) {
        console.error(
          'COOKIE CONSENT OPT-OUT FALLBACK FAILED: ',
          fallbackError
        );
      }
    }
    return undefined;
  }
};

export default getCookieConsent;
